/**
 * Scrubbing rules for Sentry payloads, shared by the server (`@sentry/node`)
 * and web (`@sentry/react`) clients.
 *
 * `sendDefaultPii: false` is the primary collection boundary — it stops both
 * SDKs from attaching cookies, credentials, request bodies, and IP addresses
 * on their own. The helpers here are the second boundary, for sensitive data
 * that reaches an event some other way: an integration that assembles
 * `event.request` itself, an auto-instrumented `fetch` breadcrumb recording
 * the URL it called, or a caller attaching a request manually.
 *
 * Both clients are wired to these same rules so the two cannot drift — a
 * leak closed here is closed on both. Applied through the error, transaction,
 * span, breadcrumb, and Replay hooks in `server/src/instrument.ts` and
 * `web/src/main.tsx`.
 *
 * # What is deliberately *not* scrubbed
 *
 * `event.extra` and non-trace error `event.contexts` pass through untouched. Those are
 * caller-supplied structured diagnostics — organization ids, Zod issue
 * lists, agent/devbox/stream ids — that production triage joins on, and
 * blanket-dropping them would blind the WARN/ERROR log contract. Each call
 * site owns what it attaches there; this module cannot tell a safe field
 * from an unsafe one without a schema.
 *
 * These functions are generic over the caller's event type rather than typed
 * against `@sentry/*`: `@reflex/shared` must not take an SDK dependency, and
 * the server and web SDKs export structurally identical but nominally
 * distinct event types.
 *
 * @module @reflex/shared/sentry-scrub
 */

/**
 * Structural subset of Sentry's `ErrorEvent` that {@link scrubSensitiveSentryData} rewrites.
 *
 * Used as an internal read shape rather than a generic constraint. Every
 * field is optional, which makes it a TypeScript *weak type*: constraining
 * on it would reject any event that happens to carry neither `request` nor
 * `user`, which is precisely the case the scrub still needs to handle.
 */
export interface ScrubbableSentryEvent {
  request?: { method?: string; url?: string };
  user?: { id?: string | number };
}

/** Structural subset of Sentry's `Breadcrumb` that {@link scrubSentryBreadcrumb} rewrites. */
export interface ScrubbableSentryBreadcrumb {
  category?: string;
  data?: Record<string, unknown>;
}

interface ScrubbableSentrySpan {
  attributes?: Record<string, unknown>;
  data?: Record<string, unknown>;
  description?: string;
  name?: string;
  op?: string;
}

interface ScrubbableSentryTransaction extends ScrubbableSentryEvent {
  contexts?: Record<string, unknown> & {
    trace?: Record<string, unknown> & { data?: Record<string, unknown> };
  };
  spans?: object[];
}

interface ScrubbableSentryReplayEvent extends ScrubbableSentryEvent {
  type?: string;
  urls?: unknown[];
}

interface ScrubbableReplayRecordingEvent {
  data?: {
    tag?: string;
    payload?: {
      data?: Record<string, unknown>;
      description?: string;
      name?: string;
      op?: string;
    };
  };
}

/**
 * Breadcrumb `data` keys that hold a URL: `fetch`/`xhr` breadcrumbs record
 * the request in `url`, `navigation` breadcrumbs record the route change in
 * `from` and `to`. All three carry the query string verbatim.
 */
const URL_BEARING_BREADCRUMB_KEYS = ['url', 'from', 'to'];

/** Node's HTTP breadcrumbs put these URL components in separate fields. */
const SENSITIVE_HTTP_BREADCRUMB_KEYS = ['http.query', 'http.fragment'];

/** Capability URLs grant access to an invite without authentication. */
const INVITE_CAPABILITY_SEGMENT = '[redacted]';

/**
 * Strip the query string, fragment, userinfo credentials, and capability-path
 * tokens from a URL, keeping only origin and a safe path.
 *
 * Parsed with `URL` rather than pattern-matched. The obvious regex —
 * `url.replace(/[?#].*$/, '')` — has two holes: `.` does not match a
 * newline, so a query string preceded by an embedded newline survives
 * intact, and it leaves `https://user:pass@host/path` untouched.
 */
function redactCapabilityPath(pathname: string): string {
  const segments = pathname.split('/');
  // `/invites/pending` is an authenticated collection route. Every other
  // second segment is the opaque token accepted by the public invite route.
  const inviteIndex = segments.findIndex((segment) => segment === 'invites');
  if (inviteIndex >= 0 && segments[inviteIndex + 1] && segments[inviteIndex + 1] !== 'pending') {
    segments[inviteIndex + 1] = INVITE_CAPABILITY_SEGMENT;
  }
  return segments.join('/');
}

/** Whether a URL is an invite capability URL and must not start Sentry Replay. */
export function hasCapabilityUrlSecret(url: string): boolean {
  try {
    const networkPath = url.startsWith('//');
    const parsed = networkPath ? new URL(url, 'https://sentry.invalid') : new URL(url);
    return redactCapabilityPath(parsed.pathname) !== parsed.pathname;
  } catch {
    const cuts = [url.indexOf('?'), url.indexOf('#')].filter((index) => index >= 0);
    const path = cuts.length > 0 ? url.slice(0, Math.min(...cuts)) : url;
    return redactCapabilityPath(path) !== path;
  }
}

export function stripUrlSecrets(url: string): string {
  try {
    const networkPath = url.startsWith('//');
    const parsed = networkPath ? new URL(url, 'https://sentry.invalid') : new URL(url);
    parsed.search = '';
    parsed.hash = '';
    parsed.username = '';
    parsed.password = '';
    parsed.pathname = redactCapabilityPath(parsed.pathname);
    return networkPath ? `//${parsed.host}${parsed.pathname}` : parsed.toString();
  } catch {
    // Relative request paths (`/api/agents?token=…`) have no origin for
    // `URL` to parse. Cut at the first delimiter by index — no regex, so an
    // embedded newline cannot hide the tail.
    const cuts = [url.indexOf('?'), url.indexOf('#')].filter((index) => index >= 0);
    const path = cuts.length > 0 ? url.slice(0, Math.min(...cuts)) : url;
    return redactCapabilityPath(path);
  }
}

/**
 * Rebuild an error event with only the non-sensitive parts of `request` and
 * `user`.
 *
 * `request` is reduced to method and scrubbed URL: those identify the
 * failing route, which is the entire diagnostic value of the block.
 * Rebuilding rather than deleting means every other field the SDK models
 * here — `headers`, `cookies`, `query_string`, and `data` (the body) — is
 * dropped by construction, including any added by a future SDK version.
 */
export function scrubSensitiveSentryData<T extends object>(event: T): T {
  const { request, user } = event as ScrubbableSentryEvent;

  return {
    ...event,
    ...(request
      ? {
          request: {
            method: request.method,
            url: request.url ? stripUrlSecrets(request.url) : undefined,
          },
        }
      : {}),
    // `ip_address: null` is not equivalent to omitting the key: Sentry's
    // ingest infers the client IP from the connection when the field is
    // absent, so the explicit null is what actually suppresses it. Emitted
    // on every event, including those that carry no user on the scope.
    user: { ...(user?.id === undefined ? {} : { id: user.id }), ip_address: null },
  } as T;
}

/**
 * Scrub a breadcrumb, or return `null` to drop it.
 *
 * Breadcrumbs are the leak `beforeSend` cannot reach on its own: the browser
 * SDK's default `breadcrumbsIntegration` records every `fetch`/`xhr` URL
 * *with its query string*, so an OAuth callback's `?code=` is stripped from
 * `event.request.url` and retained in `event.breadcrumbs[].data.url` on the
 * very same event.
 *
 * `console` breadcrumbs are dropped outright rather than scrubbed. They
 * replay whatever the application passed to `console.*` — arbitrary values
 * with no schema to scrub against — so there is no safe subset to keep.
 */
export function scrubSentryBreadcrumb<T extends object>(breadcrumb: T): T | null {
  const { category, data } = breadcrumb as ScrubbableSentryBreadcrumb;
  if (category === 'console') return null;
  if (!data) return breadcrumb;

  let scrubbed: Record<string, unknown> | undefined;
  for (const key of SENSITIVE_HTTP_BREADCRUMB_KEYS) {
    if (!(key in data)) continue;
    scrubbed ??= { ...data };
    delete scrubbed[key];
  }
  for (const key of URL_BEARING_BREADCRUMB_KEYS) {
    const value = data[key];
    if (typeof value !== 'string') continue;
    const stripped = stripUrlSecrets(value);
    if (stripped === value) continue;
    scrubbed ??= { ...data };
    scrubbed[key] = stripped;
  }

  return scrubbed ? ({ ...breadcrumb, data: scrubbed } as T) : breadcrumb;
}

/**
 * Span attributes that identify *what* an operation was without carrying
 * any of its content: method, status, templated route, path, peer host, and
 * database system/operation. Both the stable and legacy OpenTelemetry
 * semantic-convention spellings are listed because the SDK emits either
 * depending on the instrumentation version.
 *
 * Everything not listed here (or under a `sentry.` prefix) is dropped:
 * query strings (`url.query`, `http.query`), fragments, captured request
 * and response headers (`http.request.header.*`), client addresses, user
 * agents, and raw SQL (`db.statement`, `db.query.text`) all carry data
 * that has no business in a third-party system.
 */
const SAFE_SENTRY_SPAN_ATTRIBUTE_KEYS = new Set([
  // HTTP — stable semantic conventions.
  'http.request.method',
  'http.response.status_code',
  'http.route',
  'url.path',
  'url.scheme',
  'url.same_origin',
  'server.address',
  'server.port',
  'network.protocol.name',
  'network.protocol.version',
  // HTTP — legacy spellings still emitted by some instrumentations.
  'http.method',
  'http.status_code',
  'http.response_content_length',
  'http.response_transfer_size',
  'http.decoded_response_content_length',
  'net.peer.name',
  'net.peer.port',
  // Browser resource / fetch spans.
  'type',
  'resource.render_blocking_status',
  'ui.component_name',
  // Database — system and operation only, never the statement.
  'db.system',
  'db.system.name',
  'db.name',
  'db.namespace',
  'db.operation',
  'db.operation.name',
  'db.collection.name',
  'db.sql.table',
  'otel.kind',
]);

/**
 * Span attributes that hold a full URL. Retained, but reduced to origin and
 * path so the span still says *which* endpoint was called.
 */
const URL_SENTRY_SPAN_ATTRIBUTE_KEYS = new Set(['url', 'url.full', 'http.url']);

/**
 * Sentry's own attributes (`sentry.op`, `sentry.origin`, `sentry.source`,
 * `sentry.sample_rate`, …) drive routing and sampling in the product and
 * never carry request content.
 */
function isSentryOwnedAttribute(key: string): boolean {
  return key.startsWith('sentry.');
}

function keepSentrySpanAttributes(
  attributes: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!attributes) return undefined;
  const kept: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (URL_SENTRY_SPAN_ATTRIBUTE_KEYS.has(key)) {
      kept[key] = typeof value === 'string' ? stripUrlSecrets(value) : value;
    } else if (isSentryOwnedAttribute(key) || SAFE_SENTRY_SPAN_ATTRIBUTE_KEYS.has(key)) {
      kept[key] = value;
    }
  }
  return kept;
}

function scrubSpanLabel(label: string | undefined, op: string | undefined): string | undefined {
  if (!label) return label;
  // Database instrumentation copies raw SQL into `description`/`name`; span
  // attributes are not its only payload path.
  if (op === 'db' || op?.startsWith('db.')) return undefined;
  if (!op || !/^(http|resource|navigation|pageload)/.test(op)) return label;
  const methodPrefix = label.match(/^([A-Z]+\s+)(.+)$/);
  return methodPrefix
    ? `${methodPrefix[1]}${stripUrlSecrets(methodPrefix[2])}`
    : stripUrlSecrets(label);
}

/**
 * Reduce a trace span's auto-instrumentation attributes to an allowlist.
 *
 * HTTP span attributes include query strings, captured request headers, and
 * client addresses, and their key set changes with the instrumentation
 * version, so a denylist cannot be kept complete. Instead only
 * {@link SAFE_SENTRY_SPAN_ATTRIBUTE_KEYS} and `sentry.*` survive, and
 * URL-bearing attributes are kept with their query, fragment, and userinfo
 * stripped. Timing, status, and ids remain top-level span fields.
 */
export function scrubSentrySpan<T extends object>(span: T): T {
  const value = span as ScrubbableSentrySpan;
  const data = keepSentrySpanAttributes(value.data);
  const attributes = keepSentrySpanAttributes(value.attributes);
  const op =
    value.op ??
    (data?.['sentry.op'] as string | undefined) ??
    (attributes?.['sentry.op'] as string | undefined);

  const scrubbed = { ...span } as Record<string, unknown>;
  if (value.data) scrubbed.data = data;
  if (value.attributes) scrubbed.attributes = attributes;
  for (const key of ['description', 'name'] as const) {
    const label = value[key];
    if (!label) continue;
    const safeLabel = scrubSpanLabel(label, op);
    if (safeLabel) scrubbed[key] = safeLabel;
    else delete scrubbed[key];
  }
  return scrubbed as T;
}

/** Scrub the request, user, root trace attributes, and child spans on a transaction. */
export function scrubSentryTransaction<T extends object>(event: T): T {
  const scrubbed = scrubSensitiveSentryData(event);
  const { contexts, spans } = scrubbed as ScrubbableSentryTransaction;
  const trace = contexts?.trace;

  return {
    ...scrubbed,
    ...(contexts && trace
      ? {
          contexts: {
            ...contexts,
            trace: {
              ...trace,
              ...(trace.data ? { data: keepSentrySpanAttributes(trace.data) } : {}),
            },
          },
        }
      : {}),
    ...(spans ? { spans: spans.map(scrubSentrySpan) } : {}),
  };
}

/** Scrub Replay envelope metadata, which bypasses `beforeSend`. */
export function scrubSentryReplayEvent<T extends object>(event: T): T {
  const value = event as ScrubbableSentryReplayEvent;
  if (value.type !== 'replay_event') return event;
  const scrubbed = scrubSensitiveSentryData(event);
  return {
    ...scrubbed,
    ...(value.urls
      ? { urls: value.urls.map((url) => (typeof url === 'string' ? stripUrlSecrets(url) : url)) }
      : {}),
  };
}

/**
 * Scrub URL-bearing custom performance frames inside a Replay recording.
 * Replay invokes this independently of `beforeBreadcrumb` and `beforeSend`.
 */
export function scrubSentryReplayRecordingEvent<T extends object>(event: T): T {
  const value = event as ScrubbableReplayRecordingEvent;
  const payload = value.data?.tag === 'performanceSpan' ? value.data.payload : undefined;
  if (!payload) return event;

  const scrubbedPayload = {
    ...payload,
    ...(payload.description
      ? { description: scrubSpanLabel(payload.description, payload.op) }
      : {}),
    ...(payload.name ? { name: scrubSpanLabel(payload.name, payload.op) } : {}),
  };
  const previous = payload.data?.previous;
  if (typeof previous === 'string') {
    scrubbedPayload.data = { ...scrubbedPayload.data, previous: stripUrlSecrets(previous) };
  }

  return { ...event, data: { ...value.data, payload: scrubbedPayload } } as T;
}
